import React, { useState } from "react";
import { Icon } from "@iconify/react";
import { Typography } from "../../../Components/Typography";
import Card from "../../../Components/Card";
import Button from "../../../Components/Button";
import { updatePasswordWithUserId } from "../../../Utils/apiServices";
import { useAuth } from "../../../Context/authContext";

const PasswordTab = () => {
  const { user } = useAuth();
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState({ type: "", text: "" });
  const [passwordFormData, setPasswordFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  const handlePasswordChange = (field, value) => {
    setPasswordFormData((prev) => ({ ...prev, [field]: value }));
    if (passwordMessage.text) {
      setPasswordMessage({ type: "", text: "" });
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handlePasswordUpdate = async () => {
    if (!user?.id) {
      setPasswordMessage({ type: "error", text: "User ID not found. Please log in again." });
      return;
    }

    if (!passwordFormData.currentPassword.trim()) {
      setPasswordMessage({ type: "error", text: "Current password is required." });
      return;
    }

    if (!passwordFormData.newPassword.trim()) {
      setPasswordMessage({ type: "error", text: "New password is required." });
      return;
    }

    if (passwordFormData.newPassword.length <= 8) {
      setPasswordMessage({ type: "error", text: "Your new password must be more than 8 characters." });
      return;
    }

    if (passwordFormData.newPassword !== passwordFormData.confirmPassword) {
      setPasswordMessage({ type: "error", text: "New password and confirm password do not match." });
      return;
    }

    setIsUpdatingPassword(true);
    setPasswordMessage({ type: "", text: "" });

    try {
      const response = await updatePasswordWithUserId(user.id, {
        oldPassword: passwordFormData.currentPassword,
        newPassword: passwordFormData.newPassword,
      });

      if (response.success) {
        setPasswordMessage({ type: "success", text: "Password updated successfully!" });
        setPasswordFormData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
        
        setTimeout(() => {
          setPasswordMessage({ type: "", text: "" });
        }, 3000);
      } else {
        setPasswordMessage({ 
          type: "error", 
          text: response.error || "Failed to update password. Please try again." 
        });
      }
    } catch (error) {
      console.error("Error updating password:", error);
      setPasswordMessage({ 
        type: "error", 
        text: error.error || "An error occurred while updating your password." 
      });
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const handlePasswordCancel = () => {
    setPasswordFormData({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setPasswordMessage({ type: "", text: "" });
  };

  return (
    <Card className="w-full">
      <div className="flex flex-col gap-6">
        <div className="flex items-start justify-between">
          <div className="flex flex-col gap-1">
            <Typography as="h2" className="text-lg font-bold text-primary-900 text-start">
              Password
            </Typography>
            <Typography as="p" className="text-sm text-tertiary-600">
              Please enter your current password to change your password.
            </Typography>
          </div>
        </div>

        {passwordMessage.text && (
          <div
            className={`p-4 rounded-lg border ${
              passwordMessage.type === "success"
                ? "bg-green-50 border-green-200 text-green-800"
                : "bg-red-50 border-red-200 text-red-800"
            }`}
          >
            <div className="flex items-center gap-2">
              <Icon
                icon={
                  passwordMessage.type === "success"
                    ? "lucide:check-circle"
                    : "lucide:alert-circle"
                }
                className={`h-5 w-5 ${
                  passwordMessage.type === "success"
                    ? "text-green-600"
                    : "text-red-600"
                }`}
              />
              <Typography as="p" className="text-sm font-medium">
                {passwordMessage.text}
              </Typography>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-6 w-full">
          <div className="flex flex-row gap-2 justify-between max-w-4xl item-start">
            <Typography as="label" className="text-sm font-medium text-primary-900 w-full max-w-xl text-start">
              Current password <span className="text-red-500">*</span>
            </Typography>
            <div className="relative w-full">
              <input
                type={showPassword.currentPassword ? "text" : "password"}
                name="currentPassword"
                value={passwordFormData.currentPassword}
                onChange={(e) => handlePasswordChange("currentPassword", e.target.value)}
                placeholder="Enter your current password"
                className="w-full rounded-lg border border-slate-200 bg-white pl-4 pr-10 py-2.5 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility("currentPassword")}
                className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 hover:text-slate-600 focus:outline-none"
              >
                <Icon
                  icon={showPassword.currentPassword ? "lucide:eye-off" : "lucide:eye"}
                  className="h-4 w-4"
                />
              </button>
            </div>
          </div>

          <div className="flex flex-row gap-2 justify-between max-w-4xl item-start">
            <Typography as="label" className="text-sm font-medium text-primary-900 w-full max-w-xl text-start">
              New password <span className="text-red-500">*</span>
            </Typography>
            <div className="flex flex-col gap-2 w-full">
            <div className="relative">
              <input
                type={showPassword.newPassword ? "text" : "password"}
                name="newPassword"
                value={passwordFormData.newPassword}
                onChange={(e) => handlePasswordChange("newPassword", e.target.value)}
                placeholder="Enter your new password"
                className="w-full rounded-lg border border-slate-200 bg-white pl-4 pr-10 py-2.5 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility("newPassword")}
                className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 hover:text-slate-600 focus:outline-none"
              >
                <Icon
                  icon={showPassword.newPassword ? "lucide:eye-off" : "lucide:eye"}
                  className="h-4 w-4"
                />
              </button>
            </div>
            <Typography as="p" className="text-xs text-tertiary-600">
              Your new password must be more than 8 characters.
            </Typography>
            </div>
          </div>

          <div className="flex flex-row gap-2 justify-between max-w-4xl item-start">
            <Typography as="label" className="text-sm font-medium text-primary-900 w-full max-w-xl text-start">
              Confirm new password <span className="text-red-500">*</span>
            </Typography>
            <div className="relative w-full">
              <input
                type={showPassword.confirmPassword ? "text" : "password"}
                name="confirmPassword"
                value={passwordFormData.confirmPassword}
                onChange={(e) => handlePasswordChange("confirmPassword", e.target.value)}
                placeholder="Confirm your new password"
                className="w-full rounded-lg border border-slate-200 bg-white pl-4 pr-10 py-2.5 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility("confirmPassword")}
                className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 hover:text-slate-600 focus:outline-none"
              >
                <Icon
                  icon={showPassword.confirmPassword ? "lucide:eye-off" : "lucide:eye"}
                  className="h-4 w-4"
                />
              </button>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 mt-2">
            <Button
              type="button"
              variant="secondary"
              size="medium"
              onClick={handlePasswordCancel}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              size="medium"
              onClick={handlePasswordUpdate}
              disabled={isUpdatingPassword}
              loading={isUpdatingPassword}
              className="bg-purple-600 hover:bg-purple-700 focus:ring-purple-500"
            >
              Update password
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default PasswordTab;

